import React, { useState, useEffect } from 'react';
import { 
  DollarSign, ShoppingCart, Package, Users, TrendingUp, 
  AlertCircle, Clock, CheckCircle, Eye, MousePointer, BarChart3 
} from 'lucide-react';
import api from '../../utils/api';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    pendingOrders: 0,
    totalProducts: 0,
    totalCustomers: 0,
    lowStockProducts: 0
  });
  const [analytics, setAnalytics] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('7d');

  useEffect(() => {
    fetchDashboardData();
  }, [analyticsPeriod]);

  const fetchDashboardData = async () => {
    try {
      const [ordersRes, productsRes] = await Promise.all([
        api.get('/orders/admin/orders/stats'),
        api.get('/products')
      ]);

      if (ordersRes.data.success) {
        setStats(prev => ({
          ...prev,
          ...ordersRes.data.data
        }));
      }

      if (productsRes.data.success) {
        const products = productsRes.data.data;
        setStats(prev => ({
          ...prev,
          totalProducts: products.length,
          lowStockProducts: products.filter(p => p.totalStock < 10).length
        }));
      }

      // Fetch recent orders
      const ordersListRes = await api.get('/orders/admin/orders?limit=5');
      if (ordersListRes.data.success) {
        setRecentOrders(ordersListRes.data.data);
      }

      // Fetch analytics
      try {
        const analyticsRes = await api.get(`/analytics/dashboard?period=${analyticsPeriod}`);
        if (analyticsRes.data.success) {
          setAnalytics(analyticsRes.data.data);
        }
      } catch (analyticsError) {
        console.log('Analytics not available yet:', analyticsError.message);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon, title, value, color, bgColor }) => (
    <div className={`${bgColor} border-4 border-black p-6 shadow-[8px_8px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 hover:shadow-[4px_4px_0px_rgba(0,0,0,1)] transition-all`}>
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 ${color} border-2 border-black`}>
          {icon}
        </div>
      </div>
      <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">{title}</div>
      <div className="text-4xl font-black">{value}</div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-2xl font-black animate-pulse">LOADING_DASHBOARD...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-5xl font-black tracking-tighter mb-2">DASHBOARD</h1>
        <div className="inline-block bg-yellow-400 text-black px-4 py-1 font-mono font-bold text-xs border-2 border-black">
          REAL_TIME_ANALYTICS
        </div>
      </div>

      {/* Analytics Period Selector */}
      <div className="mb-6 flex gap-2">
        {['24h', '7d', '30d', '90d'].map((period) => (
          <button
            key={period}
            onClick={() => setAnalyticsPeriod(period)}
            className={`px-4 py-2 font-bold border-2 border-black transition-all ${
              analyticsPeriod === period 
                ? 'bg-yellow-400 text-black shadow-[4px_4px_0px_black]' 
                : 'bg-white hover:bg-gray-100'
            }`}
          >
            {period.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={<DollarSign size={24} />}
          title="Total Revenue"
          value={`₹${stats.totalRevenue.toLocaleString('en-IN')}`}
          color="text-green-600"
          bgColor="bg-green-50"
        />
        <StatCard
          icon={<ShoppingCart size={24} />}
          title="Total Orders"
          value={stats.totalOrders}
          color="text-blue-600"
          bgColor="bg-blue-50"
        />
        <StatCard
          icon={<Clock size={24} />}
          title="Pending Orders"
          value={stats.pendingOrders}
          color="text-orange-600"
          bgColor="bg-orange-50"
        />
        <StatCard
          icon={<Package size={24} />}
          title="Total Products"
          value={stats.totalProducts}
          color="text-purple-600"
          bgColor="bg-purple-50"
        />
      </div>

      {/* Web Analytics Section */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={<Eye size={24} />}
            title="Page Views"
            value={analytics.overview.totalPageViews.toLocaleString('en-IN')}
            color="text-indigo-600"
            bgColor="bg-indigo-50"
          />
          <StatCard
            icon={<Users size={24} />}
            title="Unique Visitors"
            value={analytics.overview.uniqueVisitors.toLocaleString('en-IN')}
            color="text-cyan-600"
            bgColor="bg-cyan-50"
          />
          <StatCard
            icon={<MousePointer size={24} />}
            title="Add to Cart"
            value={analytics.overview.addToCartEvents}
            color="text-pink-600"
            bgColor="bg-pink-50"
          />
          <StatCard
            icon={<BarChart3 size={24} />}
            title="Conversion Rate"
            value={`${analytics.overview.conversionRate}%`}
            color="text-emerald-600"
            bgColor="bg-emerald-50"
          />
        </div>
      )}

      {/* Alerts */}
      {stats.lowStockProducts > 0 && (
        <div className="mb-8 p-6 bg-red-50 border-4 border-red-600 shadow-[8px_8px_0px_rgba(220,38,38,1)]">
          <div className="flex items-start gap-4">
            <AlertCircle className="text-red-600 flex-shrink-0" size={32} />
            <div>
              <h3 className="text-xl font-black mb-1">LOW STOCK ALERT</h3>
              <p className="font-bold">{stats.lowStockProducts} products have less than 10 items in stock. Restock immediately!</p>
            </div>
          </div>
        </div>
      )}

      {/* Recent Orders */}
      <div className="bg-white border-4 border-black shadow-[8px_8px_0px_rgba(0,0,0,1)] p-6">
        <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
          <TrendingUp size={32} />
          RECENT ORDERS
        </h2>

        <div className="space-y-4">
          {recentOrders.length === 0 ? (
            <div className="text-center py-12 text-gray-400 font-bold">
              No orders yet. Start selling!
            </div>
          ) : (
            recentOrders.map((order) => (
              <div
                key={order._id}
                className="flex items-center justify-between p-4 bg-gray-50 border-2 border-black hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className={`p-2 ${
                    order.status === 'delivered' ? 'bg-green-500' :
                    order.status === 'shipped' ? 'bg-blue-500' :
                    order.status === 'processing' ? 'bg-yellow-500' :
                    'bg-gray-500'
                  } border-2 border-black`}>
                    {order.status === 'delivered' && <CheckCircle size={20} className="text-white" />}
                    {order.status === 'shipped' && <Package size={20} className="text-white" />}
                    {order.status === 'processing' && <Clock size={20} className="text-white" />}
                  </div>
                  
                  <div>
                    <div className="font-black text-lg">{order.orderNumber}</div>
                    <div className="text-sm font-mono text-gray-600">
                      {order.user?.name || 'Guest'} • {new Date(order.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <div className="font-black text-2xl">₹{order.pricing?.total?.toLocaleString('en-IN') || 0}</div>
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-600">
                    {order.status}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Analytics Breakdown */}
      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
          {/* Device Breakdown */}
          <div className="bg-white border-4 border-black shadow-[8px_8px_0px_rgba(0,0,0,1)] p-6">
            <h2 className="text-2xl font-black mb-6 flex items-center gap-3">
              <Users size={28} />
              DEVICE BREAKDOWN
            </h2>
            <div className="space-y-4">
              {analytics.breakdown.devices.map((device) => (
                <div key={device._id} className="flex items-center justify-between p-3 bg-gray-50 border-2 border-black">
                  <span className="font-bold uppercase">{device._id || 'Unknown'}</span>
                  <div className="flex items-center gap-4">
                    <div className="w-32 h-3 bg-gray-200 border border-black">
                      <div 
                        className="h-full bg-yellow-400" 
                        style={{ width: `${(device.count / analytics.overview.totalPageViews) * 100}%` }}
                      />
                    </div>
                    <span className="font-black w-16 text-right">{device.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Products */}
          <div className="bg-white border-4 border-black shadow-[8px_8px_0px_rgba(0,0,0,1)] p-6">
            <h2 className="text-2xl font-black mb-6 flex items-center gap-3">
              <TrendingUp size={28} />
              TOP VIEWED PRODUCTS
            </h2>
            <div className="space-y-3">
              {analytics.topProducts.length === 0 ? (
                <div className="text-center py-8 text-gray-400 font-bold">
                  No product views tracked yet
                </div>
              ) : (
                analytics.topProducts.slice(0, 5).map((product, idx) => (
                  <div key={product._id} className="flex items-center justify-between p-3 bg-gray-50 border-2 border-black">
                    <div className="flex items-center gap-3">
                      <span className="bg-black text-white w-8 h-8 flex items-center justify-center font-black">
                        {idx + 1}
                      </span>
                      <div>
                        <div className="font-bold">{product.name}</div>
                        <div className="text-xs text-gray-500 uppercase">{product.category}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-black">{product.views} views</div>
                      <div className="text-sm text-gray-600">₹{product.price}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Category Views */}
          {analytics.breakdown.categories.length > 0 && (
            <div className="bg-white border-4 border-black shadow-[8px_8px_0px_rgba(0,0,0,1)] p-6">
              <h2 className="text-2xl font-black mb-6 flex items-center gap-3">
                <BarChart3 size={28} />
                CATEGORY VIEWS
              </h2>
              <div className="space-y-4">
                {analytics.breakdown.categories.map((cat) => (
                  <div key={cat._id} className="flex items-center justify-between p-3 bg-gray-50 border-2 border-black">
                    <span className="font-bold uppercase">{cat._id}</span>
                    <span className="font-black bg-yellow-400 px-3 py-1 border-2 border-black">{cat.views}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Browser Stats */}
          <div className="bg-white border-4 border-black shadow-[8px_8px_0px_rgba(0,0,0,1)] p-6">
            <h2 className="text-2xl font-black mb-6 flex items-center gap-3">
              <Eye size={28} />
              BROWSER STATS
            </h2>
            <div className="space-y-4">
              {analytics.breakdown.browsers.map((browser) => (
                <div key={browser._id} className="flex items-center justify-between p-3 bg-gray-50 border-2 border-black">
                  <span className="font-bold">{browser._id || 'Unknown'}</span>
                  <span className="font-black">{browser.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
